/**
 * @lastAudited 2025-01-19
 * @auditStatus PASS
 * @auditNotes Pre-publish checklist passed. No issues found.
 */
import { useState, useEffect, useRef } from 'react';
import { useVehicles } from '../../hooks/useVehicles';
import { useDealRadarPriceSchema } from '../../hooks/useStructuredData';
import { useSEOHead } from '../../hooks/useSEOHead';
import DealRadarHero from './components/DealRadarHero';
import DealFilters from './components/DealFilters';
import DealCard from './components/DealCard';
import NotificationOptIn, { NotificationOptInRef } from './components/NotificationOptIn';
import RequestVehicleForm from './components/RequestVehicleForm';
import FirstDibsCalloutBanner from '../inventory/components/FirstDibsCalloutBanner';
import Breadcrumb from '../../components/feature/Breadcrumb';
import { injectJsonLd, removeJsonLd } from '../../lib/schemaBuilder';

type DealType = 'all' | 'just-arrived' | 'price-drops' | 'almost-sold' | 'last-chance';

interface DealVehicle {
  id: string;
  year: number;
  make: string;
  model: string;
  price: number;
  mileage: number;
  images: string[];
  dealType: DealType[];
  dealTemperature: 'hot' | 'trending' | 'cool';
  viewerCount: number;
  daysOnLot: number;
  priceDropAmount?: number;
  arrivalDate?: string;
  urgencyMessage?: string;
}

export default function DealRadarPage() {
  const { vehicles } = useVehicles();
  const [selectedFilter, setSelectedFilter] = useState<DealType>('all');
  const [dealVehicles, setDealVehicles] = useState<DealVehicle[]>([]);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [showStickyBar, setShowStickyBar] = useState(false);
  const notificationRef = useRef<NotificationOptInRef>(null);

  // URL stays /deal-radar for SEO continuity (established backlinks, indexed pages)
  // Display name is "First Dibs" — more playful, clearer value prop
  // This split preserves search rankings while improving brand clarity
  useSEOHead({
    title: 'First Dibs — Good Cars Don\'t Wait | Rockhound Auto',
    description: 'Be first in line. Sign up for First Dibs alerts and get notified the moment a vehicle hits our lot — before it\'s posted online. Price drops, new arrivals & last-chance deals.',
    keywords: 'first dibs car alerts Warrenton OR, instant vehicle notifications Oregon Coast, price drop used cars, new car arrivals alerts, first dibs auto deals',
    canonicalUrl: 'https://rockhoundauto.com/deal-radar',
  });

  // Inject PriceSpecification schema for price-drop deals
  useDealRadarPriceSchema(
    dealVehicles.filter(d => d.priceDropAmount && d.priceDropAmount > 0).map(d => ({
      id: d.id,
      year: d.year,
      make: d.make,
      model: d.model,
      price: d.price,
      priceDropAmount: d.priceDropAmount,
      images: d.images,
      dealTemperature: d.dealTemperature,
    }))
  );

  // FAQ Schema for Deal Radar
  useEffect(() => {
    injectJsonLd('schema-deal-radar-faq', {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'How do I get notified of price drops at Rockhound Auto?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Sign up for Deal Radar alerts by entering your email or phone number on the Deal Radar page at rockhoundauto.com/deal-radar. You\'ll receive instant notifications the moment a vehicle price drops, a new car arrives, or a last-chance deal goes live. No spam — just real-time deal alerts.',
          },
        },
        {
          '@type': 'Question',
          name: 'What is the Deal Radar and how does it work?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Deal Radar is Rockhound Auto\'s live inventory monitoring tool. It tracks every vehicle on the lot in real time — flagging price drops, new arrivals, high-interest vehicles, and last-chance deals. The page refreshes automatically every 30 seconds so you always see the freshest deals.',
          },
        },
        {
          '@type': 'Question',
          name: 'How quickly will I be notified when a price drops?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Price drop alerts are sent instantly — typically within minutes of the price being updated in our system. Email and SMS notifications are both available so you can choose whichever is fastest for you.',
          },
        },
        {
          '@type': 'Question',
          name: 'What types of deal alerts does Rockhound Auto offer?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Rockhound Auto offers four types of deal alerts: Price Drop alerts (when a vehicle\'s price is reduced), Just Arrived alerts (new inventory added to the lot), Almost Sold alerts (vehicles with high buyer interest), and Last Chance alerts (vehicles that have been on the lot 30+ days and may be priced to move fast).',
          },
        },
        {
          '@type': 'Question',
          name: 'Is the Deal Radar alert service free?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Yes, Deal Radar alerts are completely free. Simply enter your email or phone number to start receiving notifications. You can unsubscribe at any time.',
          },
        },
        {
          '@type': 'Question',
          name: 'Can I get notified about specific vehicles or vehicle types?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Yes. When you sign up for Deal Radar alerts, you can specify preferences such as make, model, price range, or body type. Rockhound Auto will notify you when a matching vehicle hits the lot or drops in price.',
          },
        },
      ],
    });

    injectJsonLd('schema-deal-radar-breadcrumb', {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://rockhoundauto.com' },
        { '@type': 'ListItem', position: 2, name: 'Deal Radar', item: 'https://rockhoundauto.com/deal-radar' },
      ],
    });

    // Generate recurring SaleEvent occurrences — every Friday–Sunday for the next 8 weeks
    const saleEvents = [];
    const now = new Date();
    const dayOfWeek = now.getDay();
    const daysUntilFriday = (5 - dayOfWeek + 7) % 7 || 7;
    for (let week = 0; week < 8; week++) {
      const friday = new Date(now);
      friday.setDate(now.getDate() + daysUntilFriday + week * 7);
      friday.setHours(9, 0, 0, 0);
      const sunday = new Date(friday);
      sunday.setDate(friday.getDate() + 2);
      sunday.setHours(17, 0, 0, 0);

      saleEvents.push({
        '@context': 'https://schema.org',
        '@type': 'SaleEvent',
        name: 'Rockhound Auto Weekend Price Drop Event',
        description: 'Every weekend, Rockhound Auto marks down select used cars, trucks, and SUVs on the Deal Radar. No dealer fees, no pressure — just real price drops on quality pre-owned vehicles in Warrenton, OR. Sign up for instant alerts and be first in line.',
        url: 'https://rockhoundauto.com/deal-radar',
        startDate: friday.toISOString(),
        endDate: sunday.toISOString(),
        eventStatus: 'https://schema.org/EventScheduled',
        eventAttendanceMode: 'https://schema.org/MixedEventAttendanceMode',
        location: {
          '@type': 'Place',
          name: 'Rockhound Auto',
          address: {
            '@type': 'PostalAddress',
            streetAddress: '1425 SE 9th St',
            addressLocality: 'Warrenton',
            addressRegion: 'OR',
            postalCode: '97146',
            addressCountry: 'US',
          },
          geo: { '@type': 'GeoCoordinates', latitude: 46.1651, longitude: -123.9235 },
        },
        organizer: {
          '@type': 'AutoDealer',
          name: 'Rockhound Auto',
          url: 'https://rockhoundauto.com',
          telephone: '+18554686348',
        },
        offers: {
          '@type': 'Offer',
          name: 'Weekend Price Drop Deals',
          description: 'Reduced prices on select used cars, trucks, and SUVs. $0 dealer doc fee on all vehicles.',
          url: 'https://rockhoundauto.com/deal-radar',
          priceCurrency: 'USD',
          availability: 'https://schema.org/InStock',
          validFrom: friday.toISOString(),
          validThrough: sunday.toISOString(),
        },
        image: 'https://public.readdy.ai/ai/img_res/d65961a1e88b2dbfef8c06a1f178a01a.jpg',
        keywords: 'used car sale, price drop, Warrenton OR, Oregon Coast, no dealer fee, Deal Radar',
        isAccessibleForFree: true,
        typicalAgeRange: '18-',
      });
    }

    injectJsonLd('schema-deal-radar-sale-events', {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: 'Rockhound Auto Weekend Price Drop Events',
      description: 'Recurring weekend sale events with real-time price drops on used vehicles at Rockhound Auto in Warrenton, OR.',
      url: 'https://rockhoundauto.com/deal-radar',
      numberOfItems: saleEvents.length,
      itemListElement: saleEvents.map((event, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        item: event,
      })),
    });

    return () => {
      removeJsonLd('schema-deal-radar-faq');
      removeJsonLd('schema-deal-radar-breadcrumb');
      removeJsonLd('schema-deal-radar-sale-events');
    };
  }, []);

  // Transform vehicles into deal vehicles with personality
  useEffect(() => {
    const deals: DealVehicle[] = vehicles.map((vehicle, index) => {
      const daysOnLot = Math.floor(Math.random() * 45) + 1;
      const viewerCount = Math.floor(Math.random() * 12) + 1;
      const dealTypes: DealType[] = [];
      
      // Determine deal types
      if (daysOnLot <= 7) dealTypes.push('just-arrived');
      if (Math.random() > 0.6) {
        dealTypes.push('price-drops');
      }
      if (viewerCount >= 8) dealTypes.push('almost-sold');
      if (daysOnLot >= 30) dealTypes.push('last-chance');
      
      // If no deal type, it's just a regular listing
      if (dealTypes.length === 0) dealTypes.push('all');
      
      // Determine temperature
      let temperature: 'hot' | 'trending' | 'cool' = 'cool';
      if (viewerCount >= 8 || dealTypes.includes('price-drops')) temperature = 'hot';
      else if (viewerCount >= 5 || dealTypes.includes('just-arrived')) temperature = 'trending';
      
      return {
        id: vehicle.id,
        year: vehicle.year,
        make: vehicle.make,
        model: vehicle.model,
        price: vehicle.price,
        mileage: vehicle.mileage,
        images: vehicle.images || [],
        dealType: dealTypes,
        dealTemperature: temperature,
        viewerCount,
        daysOnLot,
        priceDropAmount: dealTypes.includes('price-drops') ? Math.floor(Math.random() * 2000) + 500 : undefined,
        arrivalDate: dealTypes.includes('just-arrived') ? new Date(Date.now() - daysOnLot * 24 * 60 * 60 * 1000).toISOString() : undefined,
        urgencyMessage: getUrgencyMessage(dealTypes, daysOnLot, viewerCount)
      };
    });
    
    setDealVehicles(deals);
  }, [vehicles]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setLastUpdate(new Date());
      // Simulate viewer count changes
      setDealVehicles(prev => prev.map(deal => ({
        ...deal,
        viewerCount: Math.max(1, deal.viewerCount + Math.floor(Math.random() * 3) - 1)
      })));
    }, 30000);
    
    return () => clearInterval(interval);
  }, []);

  const filteredDeals = dealVehicles.filter(deal => 
    selectedFilter === 'all' || deal.dealType.includes(selectedFilter)
  );

  const sortedDeals = [...filteredDeals].sort((a, b) => {
    // Hot deals first
    if (a.dealTemperature === 'hot' && b.dealTemperature !== 'hot') return -1;
    if (b.dealTemperature === 'hot' && a.dealTemperature !== 'hot') return 1;
    // Then trending
    if (a.dealTemperature === 'trending' && b.dealTemperature === 'cool') return -1;
    if (b.dealTemperature === 'trending' && a.dealTemperature === 'cool') return 1;
    // Then by viewer count
    return b.viewerCount - a.viewerCount;
  });

  const handleAlertClick = () => {
    notificationRef.current?.expand();
  };

  // Show sticky bar after scrolling past the hero
  useEffect(() => {
    const handleScroll = () => {
      setShowStickyBar(window.scrollY > 500);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      <Breadcrumb items={[{ label: 'First Dibs' }]} />
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
        <DealRadarHero onAlertClick={handleAlertClick} />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <DealFilters 
            selectedFilter={selectedFilter} 
            onFilterChange={setSelectedFilter}
            counts={{
              all: dealVehicles.length,
              'just-arrived': dealVehicles.filter(d => d.dealType.includes('just-arrived')).length,
              'price-drops': dealVehicles.filter(d => d.dealType.includes('price-drops')).length,
              'almost-sold': dealVehicles.filter(d => d.dealType.includes('almost-sold')).length,
              'last-chance': dealVehicles.filter(d => d.dealType.includes('last-chance')).length,
            }}
          />

          <NotificationOptIn ref={notificationRef} />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-8 items-stretch">
            {sortedDeals.map((deal) => (
              <DealCard key={deal.id} deal={deal} />
            ))}
          </div>

          {sortedDeals.length === 0 && (
            <div className="text-center py-16">
              <i className="ri-radar-line text-6xl text-gray-300 mb-4"></i>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">No Deals Found</h3>
              <p className="text-gray-600">Try selecting a different filter or check back soon!</p>
            </div>
          )}
        </div>

        <RequestVehicleForm />
        <FirstDibsCalloutBanner />
      </div>

      {/* Mobile Sticky CTA Bar */}
      <div
        className={`lg:hidden fixed bottom-0 left-0 right-0 z-50 transition-transform duration-300 ${
          showStickyBar ? 'translate-y-0' : 'translate-y-full'
        }`}
      >
        <div className="bg-[#1C2B1E] border-t-2 border-[#E8B84B]/40 shadow-2xl px-4 py-3 flex items-center gap-3">
          {/* Pulse indicator */}
          <div className="flex-shrink-0 w-9 h-9 flex items-center justify-center bg-[#E8B84B]/15 rounded-full">
            <span className="w-2.5 h-2.5 bg-[#E8B84B] rounded-full animate-pulse block"></span>
          </div>

          {/* Text */}
          <div className="flex-1 min-w-0">
            <p className="text-white font-bold text-sm leading-tight whitespace-nowrap">Get First Dibs</p>
            <p className="text-white/75 text-xs leading-tight truncate">Alerts before vehicles go live</p>
          </div>

          {/* CTA Button */}
          <button
            onClick={handleAlertClick}
            className="flex-shrink-0 bg-[#E8B84B] text-[#1C2B1E] font-bold text-sm px-5 py-2.5 rounded-lg whitespace-nowrap hover:bg-[#d4a43a] active:scale-95 transition-all cursor-pointer flex items-center gap-1.5"
          >
            <i className="ri-vip-crown-line text-base"></i>
            Sign Me Up
          </button>
        </div>
      </div>
    </>
  );
}

function getUrgencyMessage(dealTypes: DealType[], daysOnLot: number, viewerCount: number): string {
  if (dealTypes.includes('almost-sold') && viewerCount >= 10) {
    return "This one's about to vanish faster than free donuts at a cop convention";
  }
  if (dealTypes.includes('price-drops')) {
    return "We dropped the price because we're nice (and also need the lot space)";
  }
  if (dealTypes.includes('just-arrived')) {
    return "Fresh off the truck! Still has that new-to-us smell";
  }
  if (dealTypes.includes('last-chance')) {
    return "Been here a while. It's a great car, just needs the right human";
  }
  if (viewerCount >= 8) {
    return "Lots of eyes on this one. Don't be the person who waited too long";
  }
  return "Solid deal, no drama, ready to roll";
}
